import {
  ImportEntity,
  ImportJobStatus,
  useImportJobQuery,
  useImportPreviewLazyQuery,
  useStartImportMutation,
  type ImportOptionsInput
} from '@paintscout/api'
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
  unsavedChangesConfirmation
} from '@paintscout/ui'
import type { DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import type { ApolloError } from 'apollo-client'
import { useClientOptions, useDialogs, useUser } from '@paintscout/ui-business'
import CloseIcon from '@material-ui/icons/Close'
import * as Sentry from '@sentry/react'
import { useApolloClient } from '@apollo/react-hooks'
import { NetworkStatus } from 'apollo-client'
import { useSnackbar } from 'notistack'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { scheduleDelayedRefetch } from 'shared/graphql/util/scheduleDelayedRefetch'
import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { countNoun } from './countNoun'
import ImportStepper from './ImportStepper'
import { getImportErrorMessage } from './importErrorMessages'
import { IMPORT_POLL_INTERVAL_MS } from './importLimits'
import { getImportButtonLabel, getReviewState } from './reviewState'
import ImportStep from './steps/ImportStep'
import MapFieldsStep from './steps/MapFieldsStep'
import ReviewStep from './steps/ReviewStep'
import UploadStep from './steps/UploadStep'
import { useImportWizard } from './useImportWizard'

const useStyles = makeStyles<Theme>(() => ({
  // The mock is ~820px wide; `maxWidth="md"` resolves to 958px in this theme, so the paper is
  // capped here instead. `md` is kept for the `fullScreen` breakpoint behaviour.
  paper: {
    maxWidth: 840
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 360
  },
  spacer: {
    flex: 1
  }
}))

/**
 * Extra offsets, in ms, at which the list queries are refetched on top of `scheduleDelayedRefetch`'s
 * own 2s delay — so 2s, 5s and 10s after the import finishes.
 *
 * One refetch is not enough here. A job writes up to 5,000 documents and Elasticsearch indexes them
 * without `refresh`, so a single look 2s later can still miss the tail of a large import and leave
 * the user staring at a list that does not contain what they just imported. There is no completion
 * signal from the index, so the only option is to look again a few times. Same pattern as
 * `scheduleAutomatedDealStageSync`.
 */
const EXTRA_REFETCH_OFFSETS_MS = [3000, 8000]

/** Elasticsearch lags the writes, so the list pages are refetched by name after a delay. */
const CONTACT_REFETCH_QUERIES = ['searchContacts', 'searchContactsWithIntegration']
const COMPANY_REFETCH_QUERIES = ['searchCompanies']

/**
 * When the progress screen says it cannot reach PaintScout (#24991). Any one of these is enough:
 * - the browser, or the offline Apollo client, says it is offline. `@wora/apollo-offline` then turns
 *   every poll into a `cache-only` read that returns the last job with no error, so a poll count
 *   alone would never see the outage;
 * - this many polls in a row failed;
 * - no poll succeeded for this long. This catches a request that hangs instead of failing: Apollo
 *   skips the next poll while one is still in flight, so no failure is ever counted.
 */
export const UNREACHABLE_AFTER_FAILED_POLLS = 2
export const UNREACHABLE_AFTER_MS = 15_000
const REACHABILITY_CHECK_MS = 1000

export const START_UNREACHABLE_COPY = "We can't reach PaintScout. Check your connection, then select Retry."

/**
 * A failed `startImport` with no GraphQL error is a network failure. Our client wrapper re-sends a
 * network-failed mutation through the offline store, and `onlineOnly` makes that store reject it,
 * so the error the hook sees is "Request can not be made offline", not the original network error.
 */
export function isNetworkFailure(error: unknown): boolean {
  const e = error as { networkError?: unknown; graphQLErrors?: unknown[]; message?: string } | undefined
  if (!e || (e.graphQLErrors?.length ?? 0) > 0) {
    return false
  }
  return !!e.networkError || /network error|failed to fetch|can not be made offline/i.test(e.message ?? '')
}

function getStartErrorMessage(error: unknown): string {
  return isNetworkFailure(error) ? START_UNREACHABLE_COPY : getImportErrorMessage(error as ApolloError)
}

function isOffline(client: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true
  }
  // ApolloClientOffline keeps its own flag. Feature-detected, so a plain ApolloClient is unaffected.
  const offlineClient = client as { isOnline?: () => boolean } | undefined
  return typeof offlineClient?.isOnline === 'function' && !offlineClient.isOnline()
}

/**
 * True while the job is being watched and the client cannot get fresh job state. Polling carries
 * on, so the first good poll clears it. See UNREACHABLE_AFTER_* for the rule.
 */
function useJobUnreachable(watching: boolean, networkStatus: NetworkStatus | undefined, client: unknown): boolean {
  const [unreachable, setUnreachable] = useState(false)
  const failedPollsRef = useRef(0)
  const lastSuccessAtRef = useRef(Date.now())
  const checkRef = useRef<() => void>(() => {})

  // A layout effect, not a render-phase write, and it runs before the passive effects below use it.
  useLayoutEffect(() => {
    checkRef.current = () => {
      setUnreachable(
        watching &&
          (isOffline(client) ||
            failedPollsRef.current >= UNREACHABLE_AFTER_FAILED_POLLS ||
            Date.now() - lastSuccessAtRef.current >= UNREACHABLE_AFTER_MS)
      )
    }
  })

  // Keyed on networkStatus only: each poll moves it to `poll` and then to `ready` or `error`
  // (notifyOnNetworkStatusChange), while the error object is rebuilt on every render.
  // Order matters: this effect runs before the `[watching]` one. When watching turns on in the same
  // commit as an `error`, that reset drops this one count, so the notice needs one more failed poll.
  useEffect(() => {
    if (networkStatus === NetworkStatus.ready) {
      failedPollsRef.current = 0
      lastSuccessAtRef.current = Date.now()
    } else if (networkStatus === NetworkStatus.error) {
      failedPollsRef.current += 1
    }
    checkRef.current()
  }, [networkStatus])

  useEffect(() => {
    if (!watching) {
      setUnreachable(false)
      return
    }

    // Polling just started or resumed (a hidden tab or an idle user pauses it), so the clock starts now.
    failedPollsRef.current = 0
    lastSuccessAtRef.current = Date.now()

    const check = () => checkRef.current()
    check()
    const timer = setInterval(check, REACHABILITY_CHECK_MS)
    window.addEventListener('online', check)
    window.addEventListener('offline', check)

    return () => {
      clearInterval(timer)
      window.removeEventListener('online', check)
      window.removeEventListener('offline', check)
    }
  }, [watching])

  return unreachable
}

export interface ImportDialogProps extends DialogProps {
  entity: ImportEntity
  onCancel: () => void
}

interface EntityCopy {
  noun: string
  nounPlural: string
  title: string
  viewLabel: string
}

export function getEntityCopy(entity: ImportEntity): EntityCopy {
  return entity === ImportEntity.Contact
    ? { noun: 'contact', nounPlural: 'contacts', title: 'Import contacts', viewLabel: 'View contacts' }
    : { noun: 'company', nounPlural: 'companies', title: 'Import companies', viewLabel: 'View companies' }
}

/**
 * The four-step CSV import wizard: Upload -> Map fields -> Review -> Import.
 *
 * The job runs server-side and keeps going regardless of this dialog, so closing mid-run only
 * costs visibility (there is no import history yet) — the close guard says so.
 */
export default function ImportDialog({ entity, onCancel, open, ...baseDialogProps }: ImportDialogProps) {
  const classes = useStyles()
  const { openDialog, dismissDialog } = useDialogs()
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const client = useApolloClient()
  const { options, rates, optionsSync } = useClientOptions()

  const wizard = useImportWizard(entity)
  const { state, dispatch, fieldMapping, missingRequiredFields, usedFieldKeys } = wizard
  const copy = getEntityCopy(state.entity)
  const refetchQueries = state.entity === ImportEntity.Contact ? CONTACT_REFETCH_QUERIES : COMPANY_REFETCH_QUERIES

  const [runPreview, { loading: previewLoading, error: previewError }] = useImportPreviewLazyQuery({
    fetchPolicy: 'network-only', // the mapping may have changed; a cached plan could be stale
    onCompleted: (data) => {
      if (data?.importPreview) {
        dispatch({ type: 'PREVIEW_LOADED', preview: data.importPreview })
      }
    }
  })

  const [startImport, { loading: starting, error: startError }] = useStartImportMutation({
    // Without this, a network failure lands the mutation in the offline queue, which resolves with no
    // data and replays it later: the screen sat on "Starting..." for good (#24991). Now it errors,
    // and Retry re-sends the same clientRequestId, so a job the server did create is not duplicated.
    context: { onlineOnly: true }
  })

  const completedRef = useRef(false)
  // State, not a ref: setting it re-renders the dialog, which is what drops the poll interval to 0.
  // A ref read before it is assigned left polling running after the job finished (#24951).
  const [isTerminal, setIsTerminal] = useState(false)

  const pollInterval = usePollingInterval(isTerminal ? 0 : IMPORT_POLL_INTERVAL_MS)
  const jobQuerySkipped = !state.jobId || !wizard.inlineEligible
  const { data: jobData, networkStatus: jobNetworkStatus } = useImportJobQuery({
    variables: { id: state.jobId as string },
    skip: jobQuerySkipped,
    fetchPolicy: 'network-only',
    // Each poll then re-renders as it starts and ends, which is how useJobUnreachable counts them.
    notifyOnNetworkStatusChange: true,
    pollInterval
  })

  // On a failed poll Apollo keeps the last job in `data`, so progress stays where it was.
  const job = jobData?.importJob ?? undefined

  const jobUnreachable = useJobUnreachable(
    !jobQuerySkipped && state.step === 3 && !isTerminal && pollInterval > 0,
    jobNetworkStatus,
    client
  )

  function refetchLists() {
    // The first call stays synchronous so it runs even if the dialog unmounts immediately after.
    scheduleDelayedRefetch(client, refetchQueries)
    EXTRA_REFETCH_OFFSETS_MS.forEach((delay) => {
      setTimeout(() => scheduleDelayedRefetch(client, refetchQueries), delay)
    })
  }

  // The job id whose options were already synced, so each job syncs once.
  const syncedJobIdRef = useRef<string | null>(null)

  /**
   * The job creates missing tags and lead sources in `$options` on the server (#24983). The app
   * reads tag labels from the options it loaded at startup, so a record carrying a new tag id
   * showed no tags until a reload.
   */
  function syncClientOptions() {
    if (!state.jobId || syncedJobIdRef.current === state.jobId) {
      return
    }
    const jobId = state.jobId
    syncedJobIdRef.current = jobId
    // `?.` because the context default (outside a provider) returns undefined, not a promise.
    optionsSync({ optionsRev: options?._rev ?? '', ratesRev: rates?._rev ?? '' })?.catch?.((err: unknown) => {
      Sentry.captureException(err, { extra: { importJobId: jobId } })
    })
  }

  function buildOptions(): ImportOptionsInput {
    return {
      entity: state.entity,
      source: { fileKey: state.fileKey },
      fieldMapping,
      duplicateStrategy: state.duplicateStrategy,
      tagStrategy: state.tagStrategy
    }
  }

  // Review: run the plan on entry and whenever the duplicate strategy changes (both clear `preview`).
  useEffect(() => {
    if (state.step === 2 && !state.preview && !previewLoading && state.fileKey) {
      void runPreview({ variables: { input: buildOptions() } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.step, state.preview, state.duplicateStrategy, state.fileKey])

  // Import: fire the mutation once per plan. A retry reuses the same clientRequestId, so the
  // server returns the existing job instead of creating a second one.
  async function runStartImport() {
    // `useMutation` surfaces GraphQL and network failures through `startError`, but the promise
    // still rejects. Swallow it here so the caller's `void` never becomes an unhandled rejection;
    // the Retry button reads `startError` for what to show.
    try {
      const result = await startImport({
        variables: {
          input: buildOptions(),
          clientRequestId: state.clientRequestId,
          notifyOnComplete: !wizard.inlineEligible
        }
      })

      const id = result?.data?.startImport?.id
      if (id) {
        dispatch({ type: 'JOB_STARTED', jobId: id })
      }
    } catch (err) {
      // A dropped connection is usually the user's network, and the step shows it with a Retry. It
      // still goes to Sentry, as a warning: our client wrapper turns every network error, a gateway
      // 5xx included, into "Request can not be made offline", so this is the only trace of a 5xx.
      if (isNetworkFailure(err)) {
        Sentry.captureException(err, { level: 'warning', tags: { importStart: 'network' } })
      } else {
        Sentry.captureException(err)
      }
    }
  }

  // Refresh the list page and confirm once, when the job finishes.
  useEffect(() => {
    if (job?.status === ImportJobStatus.Complete || job?.status === ImportJobStatus.Failed) {
      setIsTerminal(true)
      // A failed job may still have created tags before it stopped.
      syncClientOptions()
    }

    if (job?.status === ImportJobStatus.Complete && !completedRef.current) {
      completedRef.current = true
      refetchLists()
      enqueueSnackbar(`Imported ${countNoun(job.created + job.updated, copy.noun, copy.nounPlural)}`, {
        variant: 'success'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status])

  function closeNow() {
    // Only refetch when a job actually ran. Cancelling at Map fields or Review has written
    // nothing, so a refetch there is a pointless Elasticsearch round trip on every abandoned run.
    // A completed job already scheduled its refetches; a second set only adds more list reloads (#24951).
    if (state.jobId && !completedRef.current) {
      refetchLists()
    }
    // A background job, or one closed mid-run, creates its tags in its first seconds, so a sync
    // here usually catches them. syncClientOptions skips a job already synced at completion.
    syncClientOptions()
    onCancel()
  }

  function handleClose() {
    // Upload has nothing to lose; results and background screens are done.
    if (state.step === 0) {
      onCancel()
      return
    }

    // A failed job has stopped too — only QUEUED/RUNNING keeps going after the dialog closes. A start
    // that failed has no job to keep running, so it closes without the warning.
    const running =
      state.step === 3 &&
      (!!state.jobId || starting) &&
      wizard.inlineEligible &&
      job?.status !== ImportJobStatus.Complete &&
      job?.status !== ImportJobStatus.Failed

    if (running) {
      openDialog(ConfirmationDialog, {
        title: 'Close this window?',
        message: "The import keeps running in the background, but you won't be notified when it finishes.",
        yesLabel: 'Close anyway',
        noLabel: 'Back',
        onConfirm: () => {
          dismissDialog()
          closeNow()
        },
        onCancel: () => dismissDialog()
      })
      return
    }

    if (state.step === 1 || state.step === 2) {
      openDialog(ConfirmationDialog, {
        ...unsavedChangesConfirmation,
        onConfirm: () => {
          dismissDialog()
          onCancel()
        },
        onCancel: () => dismissDialog()
      })
      return
    }

    closeNow()
  }

  function renderBody() {
    switch (state.step) {
      case 0:
        return (
          <UploadStep
            entity={state.entity}
            onParsed={(file, result) => dispatch({ type: 'FILE_PARSED', file, result })}
          />
        )

      case 1:
        return (
          <MapFieldsStep
            fileName={state.file?.name ?? ''}
            rowCount={state.rowCount ?? 0}
            matchedCount={state.matchedCount ?? 0}
            columns={state.columns}
            fields={state.fields}
            mapping={state.mapping}
            usedFieldKeys={usedFieldKeys}
            missingRequiredFields={missingRequiredFields}
            combinedFieldColumns={wizard.combinedFieldColumns}
            entityNoun="PaintScout"
            onChangeMapping={(columnIndex, field) => dispatch({ type: 'SET_MAPPING', columnIndex, field })}
          />
        )

      case 2:
        return (
          <ReviewStep
            entity={state.entity}
            entityNoun={copy.noun}
            entityNounPlural={copy.nounPlural}
            preview={state.preview}
            previousPreview={state.previousPreview}
            loading={previewLoading}
            errorMessage={previewError ? getImportErrorMessage(previewError) : undefined}
            duplicateStrategy={state.duplicateStrategy}
            tagStrategy={state.tagStrategy}
            tagsMapped={wizard.tagsMapped}
            companyMapped={wizard.companyMapped}
            onChangeDuplicateStrategy={(duplicateStrategy) =>
              dispatch({ type: 'SET_DUPLICATE_STRATEGY', duplicateStrategy })
            }
            onChangeTagStrategy={(tagStrategy) => dispatch({ type: 'SET_TAG_STRATEGY', tagStrategy })}
            onUploadAnother={() => dispatch({ type: 'RESET' })}
          />
        )

      default:
        return (
          <ImportStep
            entity={state.entity}
            entityNoun={copy.noun}
            entityNounPlural={copy.nounPlural}
            inlineEligible={wizard.inlineEligible}
            readyCount={state.preview?.readyCount ?? 0}
            userEmail={user?.email}
            job={job}
            preview={state.preview}
            starting={starting}
            errorMessage={startError ? getStartErrorMessage(startError) : undefined}
            unreachable={jobUnreachable}
            onRetry={() => void runStartImport()}
            doneLabel={wizard.inlineEligible && job?.status === ImportJobStatus.Complete ? copy.viewLabel : 'Done'}
            onImportAnother={() => {
              completedRef.current = false
              dispatch({ type: 'RESET' })
            }}
            onDone={closeNow}
          />
        )
    }
  }

  function renderActions() {
    // Upload drives itself: the file picker is the only control.
    if (state.step === 0) {
      return null
    }

    if (state.step === 1) {
      const blocked = missingRequiredFields.length > 0

      return (
        <DialogActions
          leftButton={
            <Button variant="text" onClick={() => dispatch({ type: 'BACK' })}>
              Back
            </Button>
          }
        >
          <Button variant="contained" disabled={blocked} onClick={() => dispatch({ type: 'ENTER_REVIEW' })}>
            Continue to review
          </Button>
        </DialogActions>
      )
    }

    if (state.step === 2) {
      const review = getReviewState({
        preview: state.preview,
        previousPreview: state.previousPreview,
        loading: previewLoading,
        hasError: !!previewError
      })
      const loadedPreview = review.reloading ? undefined : review.layoutPreview
      const back = (
        <Button variant="text" onClick={() => dispatch({ type: 'BACK' })}>
          Back
        </Button>
      )

      // Nothing to import (#25038): the job would write nothing, so Done just closes. Nothing is
      // lost by closing here, so there is no unsaved-changes prompt.
      if (review.view === 'nothingToImport') {
        return (
          <DialogActions leftButton={back}>
            <Button variant="contained" onClick={onCancel}>
              Done
            </Button>
          </DialogActions>
        )
      }

      return (
        <DialogActions leftButton={back}>
          <Button
            variant="contained"
            disabled={!loadedPreview}
            onClick={() => {
              dispatch({ type: 'ENTER_IMPORT' })
              void runStartImport()
            }}
          >
            {getImportButtonLabel(loadedPreview, copy)}
          </Button>
        </DialogActions>
      )
    }

    // Import has no footer at all: its terminal screens carry their own centred button pair in the
    // dialog body, and while the job runs there is nothing to offer.
    return null
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: classes.paper }}
      {...baseDialogProps}
    >
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        }
      >
        <Typography variant="h3">{copy.title}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <ImportStepper activeStep={state.step} />
        {renderBody()}
      </DialogContent>
      {renderActions()}
    </Dialog>
  )
}
